import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Icon } from '@/components/Icon'
import { Picker } from '@/components/Picker'
import { useBoardData } from '@/components/useBoardData'
import { PoolPanel, RemiTelemetry } from '@/components/runs/PoolPanel'
import { PoolCapacity } from '@/components/runs/PoolCapacity'
import { RecentExperiments } from '@/components/runs/ExperimentList'
import { RunBoardHeader, TaskBoardRow, WorkflowBoardRow, type RunBoardData } from '@/components/runs/RunBoard'
import { TaskRunInspector } from '@/components/runs/TaskRunInspector'
import { PageButtons } from '@/components/runs/TaskRolloutCards'
import { queryPath, type PoolSummary } from '@/lib/task-runs'
import { REVIEW_STAGES } from '@/lib/stages'
import { useAuth } from '@/auth/AuthContext'
import { api } from '@/api/client'
import { config } from '@/lib/config'
import './task-runs.css'
import './runs.css'
import './runs-overview.css'

export default function Runs() {
  const { user } = useAuth()
  const dismissalKey = JSON.stringify(['dismissed-preparation-failures', config.apiUrl, user?.id])
  const [dismissed, setDismissed] = useState<string[]>([])
  const [expandedWorkflows, setExpandedWorkflows] = useState<Set<string>>(() => new Set())
  const toggleWorkflow = (id: string) => setExpandedWorkflows(old => {
    const next = new Set(old)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(dismissalKey) ?? '[]')
      setDismissed(Array.isArray(saved) ? saved.filter(id => typeof id === 'string') : [])
    } catch { setDismissed([]) }
  }, [dismissalKey])
  const [showFailures, setShowFailures] = useState(false)
  const [dismissing, setDismissing] = useState(false)
  const [dismissError, setDismissError] = useState<string | null>(null)
  const dismiss = async (ids: string[]) => {
    setDismissing(true)
    setDismissError(null)
    const results = await Promise.allSettled(ids.map(async id => {
      await api.post(`/eng/runs/preparation-failures/${encodeURIComponent(id)}/dismiss`)
      setDismissed(old => {
        const next = [...new Set([...old, id])]
        try { localStorage.setItem(dismissalKey, JSON.stringify(next)) } catch { /* Server acknowledgment is durable. */ }
        return next
      })
    }))
    if (results.some(result => result.status === 'rejected')) setDismissError('Could not dismiss some failures. Please try again.')
    setDismissing(false)
    void failures.reload(true)
  }
  const [params, setParams] = useSearchParams()
  const query = params.get('q') ?? ''
  const [search, setSearch] = useState(query)
  const stage = params.get('stage') ?? ''
  const status = params.get('job_state') ?? ''
  const offset = Math.max(0, Number(params.get('offset') ?? 0) || 0)
  const filter = (key: string, value: string) => setParams(old => { const next = new URLSearchParams(old); if (value) next.set(key, value); else next.delete(key); next.delete('offset'); return next }, { replace: true })
  const setOffset = (value: number) => setParams(old => { const next = new URLSearchParams(old); if (value) next.set('offset', String(value)); else next.delete('offset'); return next }, { replace: true })
  const requestedRange = params.get('window') ?? '30m'
  const range = ['30m','1h','24h','all'].includes(requestedRange) ? requestedRange : '30m'
  const pools = useBoardData<{ pools: PoolSummary[]; preparing_experiments?: {id: string; name: string}[] }>(queryPath('/eng/runs/pools', { window: range }))
  const failures = useBoardData<{experiments: {id: string; name: string; failure_reason: string}[]}>('/eng/runs/preparation-failures')
  const visibleFailures = failures.data?.experiments.filter(e => !dismissed.includes(e.id)) ?? []
  // Pool records outlive their machines; an unretired record may still be historical.
  const activePools = pools.data?.pools.filter(p =>
    p.jobs.running > 0 || p.jobs.queued > 0 ||
    p.nodes.idle > 0 || p.nodes.running > 0 || p.nodes.requested > 0)
  const running = activePools?.reduce((sum, p) => sum + p.jobs.running, 0) ?? 0
  const queued = activePools?.reduce((sum, p) => sum + p.jobs.queued, 0) ?? 0
  const workflow = params.get('workflow') ?? ''
  const filters = { search: query, stage, status }
  const tasks = useBoardData<RunBoardData>(queryPath('/eng/runs/task-board', { ...filters, offset, workflow, group: workflow ? undefined : 'workflow' }))
  useEffect(() => { setSearch(query) }, [query])
  useEffect(() => {
    if (search === query) return
    const timer = window.setTimeout(() => setParams(old => { const next = new URLSearchParams(old); if (search) next.set('q', search); else next.delete('q'); next.delete('offset'); return next }, { replace: true }), 250)
    return () => clearTimeout(timer)
  }, [search, query, setParams])
  useEffect(() => {
    const timer = window.setInterval(() => { if (!document.hidden) { void pools.reload(); void tasks.reload() } }, 10000)
    return () => clearInterval(timer)
  }, [pools.reload, tasks.reload])
  useEffect(() => {
    const timer = window.setInterval(() => { if (!document.hidden) void failures.reload() }, 15000)
    return () => clearInterval(timer)
  }, [failures.reload])
  const pool = pools.data?.pools.find(p => String(p.id) === params.get('pool'))
  const open = (view: string, poolId?: number, state?: string) => setParams(old => {
    const next = new URLSearchParams(old)
    for (const key of ['experiment', 'job', 'attempt', 'pool', 'status', 'machine']) next.delete(key)
    next.set('view', view)
    if (poolId != null) next.set('pool', String(poolId))
    if (state) next.set('status', state)
    return next
  })
  const close = () => setParams(old => { const next = new URLSearchParams(old); for (const key of ['view', 'pool', 'status', 'experiment', 'job', 'attempt', 'machine']) next.delete(key); return next })
  const back = () => setParams(old => { const next = new URLSearchParams(old); for (const key of ['experiment', 'job', 'attempt']) next.delete(key); return next })
  return <div className="task-runs-overview">
    <div className="page-head"><h1>Runs</h1><Link className="btn primary" to="/eng/runs/new"><Icon name="plus" size={16} />Run tasks</Link></div>
    <RemiTelemetry window={range} onWindowChange={value => setParams(old => { const next = new URLSearchParams(old); next.set('window', value); return next })} />
    <section aria-labelledby="execution-heading" className="run-execution">
      <div className="task-section-head"><div className="execution-heading"><h2 id="execution-heading">Execution</h2>
        <details className="capacity-help"><summary aria-label="About machines and slots"><Icon name="info" size={15} /></summary><p>Machines run your rollouts. Each slot runs one rollout at a time.</p></details>
      </div>{pools.data && <span className="execution-live">{running.toLocaleString()} running · {queued.toLocaleString()} queued</span>}</div>
      {!!pools.data?.preparing_experiments?.length && <section aria-label="Preparing experiments"><h3>Preparing experiments</h3>{pools.data.preparing_experiments.map(e => <p key={e.id}><Link to={`/eng/runs/${encodeURIComponent(e.id)}`}>{e.name}</Link> · Preparing jobs</p>)}</section>}
      {!!visibleFailures.length && <section className="preparation-failures" aria-label="Failed experiment preparation">
        <div className="preparation-notice-bar">
          <button className="preparation-toggle" aria-expanded={showFailures} aria-controls="preparation-failure-list" onClick={() => setShowFailures(value => !value)}>
            <Icon name="alert" size={15} /><span>{visibleFailures.length} {visibleFailures.length === 1 ? 'run' : 'runs'} couldn’t start</span><Icon name="chevron" size={14} />
          </button>
          <button className="preparation-clear" disabled={dismissing} onClick={() => void dismiss(visibleFailures.map(e => e.id))}>Dismiss all</button>
        </div>
        {showFailures && <div id="preparation-failure-list">
          {visibleFailures.map(e => <div className="preparation-failure" key={e.id}>
            <div className="preparation-failure-content"><Link to={`/eng/runs/${encodeURIComponent(e.id)}`}>{e.name}</Link>
              <p className="preparation-reason">{e.failure_reason || 'No additional details available.'}</p>
            </div>
            <button className="preparation-close" disabled={dismissing} title={`Dismiss ${e.name}`} aria-label={`Dismiss ${e.name}`} onClick={() => void dismiss([e.id])}><Icon name="close" size={16} /></button>
          </div>)}
        </div>}

      </section>}
      {dismissError && <p role="alert" className="note err">{dismissError}</p>}
      {failures.error && <p className="note err">Could not load preparation failures: {failures.error}</p>}
      {pools.error && <p className="note err">{pools.error}</p>}
      {!pools.data && !pools.error && <p className="muted">Loading pools…</p>}
      {!!activePools?.length && <div className="capacity-table">
        <div className="capacity-table-head" aria-hidden="true"><span>Environment</span><span>Machines</span><span>Slots</span><span>Rollouts</span><span /></div>
        {activePools.map(p => <PoolCapacity key={p.id} pool={p} onOpen={open} />)}
      </div>}
      {pools.data && !activePools?.length && <p className="empty">No execution pools available.</p>}
    </section>
    <RecentExperiments />
    <section aria-labelledby="jobs-heading">
      {params.get('workflow') && <p className="muted small">Showing this workflow’s tasks. <Link to="/eng/runs">Show all tasks</Link></p>}
      <div className="task-section-head"><h2 id="jobs-heading">Workflows &amp; tasks <span className="section-count" aria-hidden="true">{tasks.data?.total ?? tasks.data?.tasks.length ?? '—'}</span></h2></div>
      <div className="task-board-toolbar">
        <label className="task-search"><Icon name="search" size={16} /><input type="search" aria-label="Search tasks" placeholder="Find a workflow or task…" value={search} onChange={event => setSearch(event.target.value)} /></label>
        <Picker label="Task stage" field value={stage} onChange={value => filter('stage', value)}
          options={[{ value: '', label: 'All stages' }, ...REVIEW_STAGES.map(s => ({ value: String(s.stage), label: s.label }))]} />
        <Picker label="Execution status" field value={status} onChange={value => filter('job_state', value)}
          options={[{ value: '', label: 'All statuses' }, ...['running', 'queued', 'completed', 'failed', 'terminated'].map(value => ({ value, label: value }))]} />
      </div>
      {tasks.error && <p className="note err">{tasks.error}</p>}
      {!tasks.data && !tasks.error && <p className="muted">Loading tasks…</p>}
      <div className="run-board"><RunBoardHeader />
        {tasks.data?.tasks.map(task => task.entry_type === 'workflow'
          ? <WorkflowBoardRow task={task} filters={filters} key={task.task_id} expanded={expandedWorkflows.has(task.task_id)} onToggle={() => toggleWorkflow(task.task_id)} />
          : <TaskBoardRow task={task} key={task.task_id} />)}
      </div>
      {tasks.data && !tasks.data.tasks.length && <p className="empty">No matching tasks with model rollout history.</p>}
      {tasks.data && <PageButtons offset={offset} next={tasks.data.next_offset} onChange={setOffset} />}
    </section>
    {params.get('experiment') && params.get('job') ? <TaskRunInspector experiment={params.get('experiment')!} job={params.get('job')!}
      attempt={params.get('attempt') ?? undefined} onClose={close} onBack={back} onAttempt={value => setParams(old => { const next = new URLSearchParams(old); next.set('attempt', value); return next }, { replace: true })} />
      : ['jobs','nodes'].includes(params.get('view') ?? '') && pool && <PoolPanel pool={pool} view={params.get('view')!} status={params.get('status') ?? undefined} window={range} onClose={close}
        machine={params.get('machine') ?? undefined} onMachine={value => setParams(old => { const next = new URLSearchParams(old); if (value) next.set('machine', value); else next.delete('machine'); return next }, { replace: true })}
        onStatus={value => setParams(old => { const next = new URLSearchParams(old); next.delete('machine'); if (value) next.set('status', value); else next.delete('status'); return next }, { replace: true })}
        onSelect={j => setParams(old => { const next = new URLSearchParams(old); next.set('experiment', j.experiment_id); next.set('job', j.job_id); if (j.attempt_id) next.set('attempt', j.attempt_id); else next.delete('attempt'); return next })} />}
  </div>
}
